/**
 * ElementLibraryService.ts
 * Service pour gérer la bibliothèque d'éléments (prédéfinis + personnalisés)
 */
import { AssetsService } from './AssetsService';
import { supabase, isSupabaseConfigured } from './SupabaseService';
import { logger } from '../utils/ProfessionalLogger';
import { AppError } from '../utils/AppError';

export interface ElementData {
  id: string;
  name: string;
  category: 'shapes' | 'arrows' | 'badges' | 'bubbles' | 'lines' | 'custom';
  type: 'svg' | 'png' | 'jpg' | 'eps';
  url: string;
  thumbnail?: string;
  isCustom: boolean;
  createdAt?: number;
}

/**
 * Éléments prédéfinis (formes de base)
 */
const PREDEFINED_ELEMENTS: Omit<ElementData, 'id' | 'isCustom' | 'createdAt'>[] = [
  // Formes de base
  {
    name: 'Rectangle',
    category: 'shapes',
    type: 'svg',
    url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iIzNiODJmNiIvPjwvc3ZnPg==',
  },
  {
    name: 'Cercle',
    category: 'shapes',
    type: 'svg',
    url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSI1MCIgY3k9IjUwIiByPSI1MCIgZmlsbD0iIzEwYjk4MSIvPjwvc3ZnPg==',
  },
  {
    name: 'Triangle',
    category: 'shapes',
    type: 'svg',
    url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cG9seWdvbiBwb2ludHM9IjUwLDEwIDkwLDkwIDEwLDkwIiBmaWxsPSIjZjU5ZTBiIi8+PC9zdmc+',
  },
  {
    name: 'Étoile',
    category: 'shapes',
    type: 'svg',
    url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cG9seWdvbiBwb2ludHM9IjUwLDUgNjEsMzggOTUsMzggNjgsNTkgNzksOTUgNTAsNzIgMjEsOTUgMzIsNTkgNSwzOCAzOSwzOCIgZmlsbD0iI2VhYjMwOCIvPjwvc3ZnPg==',
  },
  {
    name: 'Cœur',
    category: 'shapes',
    type: 'svg',
    url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNNTAsODAgQzQwLDcwIDIwLDUwIDIwLDM1IEMyMCwyNSAzMCwxNSA0MCwxNSBDNDUsMTUgNTAsMTggNTIsMjIgQzU0LDE4IDU5LDE1IDY0LDE1IEM3NCwxNSA4NCwyNSA4NCwzNSBDODQsNTAgNjQsNzAgNTQsODAgWiIgZmlsbD0iI2VmNDQ0NCIvPjwvc3ZnPg==',
  },

  // Flèches
  {
    name: 'Flèche droite',
    category: 'arrows',
    type: 'svg',
    url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTAuNTAsNTAgTDcwLDUwIEw1NSwzNSBMODAsNTAgTDU1LDY1IEw3MCw1MCBaIiBmaWxsPSIjM2I4MmY2Ii8+PC9zdmc+',
  },
  {
    name: 'Flèche gauche',
    category: 'arrows',
    type: 'svg',
    url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNODkuNTAsNTAgTDMwLDUwIEw0NSwzNSBMMTAsNTAgTDQ1LDY1IEwzMCw1MCBaIiBmaWxsPSIjM2I4MmY2Ii8+PC9zdmc+',
  },
  {
    name: 'Flèche haut',
    category: 'arrows',
    type: 'svg',
    url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNNTAsMTAuNTAgTDUwLDcwIEwzNSw1NSBMNTAsODAgTDY1LDU1IEw1MCw3MCBaIiBmaWxsPSIjM2I4MmY2Ii8+PC9zdmc+',
  },
  {
    name: 'Flèche bas',
    category: 'arrows',
    type: 'svg',
    url: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNNTAsODkuNTAgTDUwLDMwIEw2NSw0NSBMNTAsMTAgTDM1LDQ1IEw1MCwzMCBaIiBmaWxsPSIjM2I4MmY2Ii8+PC9zdmc+',
  },
];

export class ElementLibraryService {
  private static instance: ElementLibraryService | null = null;
  private customElements: Map<string, ElementData> = new Map();

  public static getInstance(): ElementLibraryService {
    if (!ElementLibraryService.instance) {
      ElementLibraryService.instance = new ElementLibraryService();
    }
    return ElementLibraryService.instance;
  }

  /**
   * Récupère tous les éléments (prédéfinis + personnalisés)
   */
  public async getElements(category?: ElementData['category']): Promise<ElementData[]> {
    const predefined: ElementData[] = PREDEFINED_ELEMENTS.map((el, index) => ({
      ...el,
      id: `predefined_${index}`,
      isCustom: false,
      createdAt: 0,
    }));

    // Charger les éléments personnalisés depuis Supabase
    const custom = await this.loadCustomElements();

    const allElements = [...predefined, ...custom];

    if (category) {
      return allElements.filter(el => el.category === category);
    }

    return allElements;
  }

  /**
   * Ajoute un élément personnalisé
   */
  public async addCustomElement(file: File, name?: string): Promise<ElementData> {
    if (!isSupabaseConfigured()) {
      throw new AppError('SUPABASE_ERROR', 'Supabase non configuré');
    }

    try {
      // Uploader le fichier via AssetsService
      const assetsService = new AssetsService();
      const result = await assetsService.uploadAsset({
        file,
        bucket: 'user-uploads',
        projectId: undefined, // Éléments globaux, pas liés à un projet
      });

      if (result.error || !result.data) {
        throw new AppError('UPLOAD_ERROR', "Erreur lors de l'upload du fichier");
      }

      // Déterminer le type de fichier
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      let elementType: ElementData['type'] = 'png';
      if (fileExtension === 'svg') elementType = 'svg';
      else if (fileExtension === 'eps') elementType = 'eps';
      else if (fileExtension === 'jpg' || fileExtension === 'jpeg') elementType = 'jpg';

      // Déterminer la catégorie (pour l'instant, toujours 'custom')
      const category: ElementData['category'] = 'custom';

      const element: ElementData = {
        id: `custom_${Date.now()}_${Math.random().toString(36).substring(7)}`,
        name: name || file.name.replace(/\.[^/.]+$/, ''),
        category,
        type: elementType,
        url: result.data.url,
        thumbnail: result.data.url, // Pour l'instant, utiliser l'URL principale comme thumbnail
        isCustom: true,
        createdAt: Date.now(),
      };

      // Sauvegarder dans Supabase (table template_editor_elements)
      if (supabase) {
        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        if (!userError && user) {
          await // P3 : table `template_editor_elements` non créée dans les migrations actuelles.
          // On bypass le type-check ; si la table n'existe pas, l'erreur est catch plus bas.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (supabase as any).from('template_editor_elements').insert({
            user_id: user.id,
            name: element.name,
            category: element.category,
            type: element.type,
            url: element.url,
            thumbnail_url: element.thumbnail,
            storage_path: result.data.path,
            created_at: new Date().toISOString(),
          });
        }
      }

      // Ajouter au cache local
      this.customElements.set(element.id, element);

      logger.info(`✅ Élément personnalisé ajouté: ${element.name}`);
      return element;
    } catch (error) {
      logger.error("Erreur lors de l'ajout de l'élément personnalisé:", error);
      if (error instanceof AppError) {
        throw error;
      }
      throw new AppError(
        'ADD_ELEMENT_ERROR',
        "Erreur lors de l'ajout de l'élément",
        {},
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  /**
   * Charge les éléments personnalisés depuis Supabase
   */
  private async loadCustomElements(): Promise<ElementData[]> {
    if (!isSupabaseConfigured() || !supabase) {
      return [];
    }

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        return [];
      }

      // P3 : table inexistante, bypass type-check.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('template_editor_elements')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        logger.warn('Erreur lors du chargement des éléments personnalisés:', error);
        return [];
      }

      interface APIElementItem {
        id: string;
        name: string;
        category?: string;
        url?: string;
        thumbnail?: string;
        type?: string;
        [key: string]: unknown;
      }

      const elements: ElementData[] = ((data ?? []) as unknown as APIElementItem[]).map(item => ({
        id: item.id,
        name: item.name,
        category: item.category || 'custom',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        type: (item.type as any) || 'png',
        url: item.url ?? '',
        thumbnail: (item.thumbnail_url as string) || item.url || '',
        isCustom: true,
        createdAt: new Date(item.created_at as string).getTime(),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      })) as any as ElementData[];

      // Mettre à jour le cache local
      elements.forEach(el => {
        this.customElements.set(el.id, el);
      });

      return elements;
    } catch (error) {
      logger.error('Erreur lors du chargement des éléments personnalisés:', error);
      return [];
    }
  }

  /**
   * Supprime un élément personnalisé
   */
  public async deleteElement(elementId: string): Promise<boolean> {
    if (!isSupabaseConfigured() || !supabase) {
      throw new AppError('SUPABASE_ERROR', 'Supabase non configuré');
    }

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new AppError('AUTH_ERROR', 'Utilisateur non authentifié');
      }

      // Supprimer de Supabase (table non typée — voir loadCustomElements)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from('template_editor_elements')
        .delete()
        .eq('id', elementId)
        .eq('user_id', user.id);

      if (error) {
        throw new AppError('SUPABASE_ERROR', error.message, { error });
      }

      // Supprimer du cache local
      this.customElements.delete(elementId);

      logger.info(`🗑️ Élément supprimé: ${elementId}`);
      return true;
    } catch (error) {
      logger.error("Erreur lors de la suppression de l'élément:", error);
      if (error instanceof AppError) {
        throw error;
      }
      throw new AppError(
        'DELETE_ELEMENT_ERROR',
        "Erreur lors de la suppression de l'élément",
        {},
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }
}

// Export singleton instance
export const elementLibraryService = ElementLibraryService.getInstance();
